




































































































/** Пустой массив здесь значит «уровень не участвует», realValuesOrEmpty к нему не применяем */
import { Modal } from 'ant-design-vue';
import { Component, Mixins, Prop, Watch } from 'vue-property-decorator';

import Toast from '@/mixins/toast';

import MmHintedButton from '../../../components/MmHintedButton.vue';
import MmLevelSelect from '../../../components/MmLevelSelect.vue';
import GroupExamplesPopover from './GroupExamplesPopover.vue';

import { fetchProductLevels } from '../../../api/catalog';
import { createCancelSource } from '../../../api/mmHttp';
import { isCanceled } from '../../../core/errors';
import { MmSelectOption } from '../../../types/domain';
import { MmProductLevelsRequest } from '../../../types/dto';
import { MmTaskCreateLevels } from '../buildTaskCreatePayload';
import { MmTaskTypeProfile } from '../taskTypeProfile';

const KEY = 'priceMonitoring.mobileMonitoring.';

/** Имена уровней совпадают с типом запроса один в один */
type MmProductLevelType = 'group' | 'category' | 'subcategory';

interface MmSubcategoriesState {
	loading: boolean;
	options: MmSelectOption[];
}

@Component({
	components: {
		MmHintedButton,
		MmLevelSelect,
		GroupExamplesPopover,
	},
})
export default class ProductFiltersBlock extends Mixins(Toast) {
	@Prop({ default: null }) readonly tradingId!: number | null;

	@Prop({ required: true }) readonly value!: MmTaskCreateLevels;

	@Prop({ default: null }) readonly profile!: MmTaskTypeProfile | null;

	@Prop({ default: false }) readonly disabled!: boolean;

	@Prop({ default: false }) readonly zonePending!: boolean;

	@Prop({ default: false }) readonly formatEmpty!: boolean;

	@Prop({ default: false }) readonly loading!: boolean;

	draft: MmTaskCreateLevels = {
		groups: [],
		categories: [],
		subCategories: [],
	};

	groupOptions: MmSelectOption[] = [];
	categoryOptions: MmSelectOption[] = [];
	subcategoryOptions: MmSelectOption[] = [];

	isGroupsLoading: boolean = false;
	isCategoriesLoading: boolean = false;
	isSubcategoriesLoading: boolean = false;

	private groupsSource: any = null;
	private categoriesSource: any = null;
	private subcategoriesSource: any = null;

	mounted(): void {
		this.syncDraftFromValue();

		/** Состояние подкатегорий сообщаем сразу: форма должна отличать загрузку от «их нет» */
		this.emitSubcategoriesState();

		if (this.tradingId === null) {
			return;
		}

		this.reloadAll(this.value.groups, this.value.categories);
	}

	beforeDestroy(): void {
		this.cancelPendingRequests();
	}

	@Watch('value', { deep: true })
	onValueChanged(): void {
		this.syncDraftFromValue();
	}

	/** Смена сети обнуляет выбор: идентификаторы групп и категорий принадлежат конкретной сети */
	@Watch('tradingId')
	onTradingIdChanged(next: number | null): void {
		this.cancelPendingRequests();

		this.groupOptions = [];
		this.categoryOptions = [];
		this.setSubcategoryOptions([]);

		this.emitLevels([], [], []);

		if (next === null) {
			return;
		}

		this.reloadAll([], []);
	}

	get hasProfile(): boolean {
		return this.profile !== null;
	}

	get titleText(): string {
		return this.profile !== null ? (this.$t(this.profile.productFiltersTitleKey) as string) : '';
	}

	get hintText(): string {
		return this.profile !== null ? (this.$t(this.profile.productFiltersHintKey) as string) : '';
	}

	get isDisabled(): boolean {
		return this.disabled || this.tradingId === null;
	}

	get isChildLevelsDisabled(): boolean {
		return this.isDisabled || this.draft.groups.length === 0;
	}

	get hasExamples(): boolean {
		return this.profile !== null && this.profile.hasProductsTable === false;
	}

	get needsLevelResetConfirm(): boolean {
		return this.profile !== null && this.profile.hasProductsTable === false;
	}

	get hasLevelsPending(): boolean {
		return !this.levelsEqual(this.draft, this.value);
	}

	get hasPendingChanges(): boolean {
		return this.hasLevelsPending || this.zonePending === true;
	}

	get hasHintIcon(): boolean {
		return this.profile !== null && this.profile.hasProductsTable === false;
	}

	get busyReasonText(): string {
		if (!this.disabled) {
			return '';
		}

		return this.$t(KEY + 'taskForm.blockSubmitting') as string;
	}

	get applyHintText(): string {
		if (!this.formatEmpty) {
			return '';
		}

		return this.$t(KEY + 'taskForm.formatRequired') as string;
	}

	handleReset(): void {
		this.syncDraftFromValue();
		this.reloadAll(this.value.groups, this.value.categories);
		this.$emit('reset-all');
	}

	handleApply(): void {
		if (this.loading) {
			return;
		}

		this.$emit('apply-all', this.cloneLevels(this.draft));
	}

	/** Дочерние справочники грузим по новому значению: проп обновится только после эмита */
	handleGroupsChange(next: number[]): void {
		const value = next ? next : [];

		if (this.isSameIdList(value, this.draft.groups)) {
			return;
		}

		this.patchDraft(value, [], []);
		this.loadCategories(value);
		this.loadSubcategories(value, []);
	}

	handleCategoriesChange(next: number[]): void {
		const value = next ? next : [];

		if (this.isSameIdList(value, this.draft.categories)) {
			return;
		}

		const isExplicitReset = value.length === 0;

		if (isExplicitReset && this.draft.subCategories.length > 0 && this.needsLevelResetConfirm) {
			Modal.confirm({
				title: this.$t(KEY + 'taskForm.confirmLevelReset') as string,
				centered: true,
				okText: this.$t(KEY + 'taskForm.confirmContinue') as string,
				cancelText: this.$t('shared.actions.cancel') as string,
				onOk: () => {
					this.applyCategories(value);
				},
			});

			return;
		}

		this.applyCategories(value);
	}

	handleSubcategoriesChange(next: number[]): void {
		const value = next ? next : [];

		if (this.isSameIdList(value, this.draft.subCategories)) {
			return;
		}

		this.patchDraft(this.draft.groups, this.draft.categories, value);
	}

	private applyCategories(value: number[]): void {
		this.patchDraft(this.draft.groups, value, []);
		this.loadSubcategories(this.draft.groups, value);
	}

	private reloadAll(groups: number[], categories: number[]): void {
		this.loadGroups();
		this.loadCategories(groups);
		this.loadSubcategories(groups, categories);
	}

	private buildRequest(
		type: MmProductLevelType,
		groups: number[],
		categories: number[],
	): MmProductLevelsRequest {
		return {
			tradingNetworkId: this.tradingId as number,
			type: type,
			groups: type === 'group' ? [] : groups.slice(),
			categories: type === 'subcategory' ? categories.slice() : [],
		};
	}

	private loadGroups(): void {
		if (this.tradingId === null) {
			return;
		}

		if (this.groupsSource !== null) {
			this.groupsSource.cancel();
		}

		const source = createCancelSource();

		this.groupsSource = source;
		this.isGroupsLoading = true;

		fetchProductLevels(this.buildRequest('group', [], []), { cancelToken: source.token })
			.then((response) => {
				if (this.groupsSource !== source) {
					return;
				}

				this.groupOptions = this.readItems(response);
			})
			.catch((error) => {
				if (isCanceled(error) || this.groupsSource !== source) {
					return;
				}

				this.groupOptions = this.emptyOptions();
				this.showError(this.$t(KEY + 'taskForm.levelsLoadError') as string);
			})
			.finally(() => {
				if (this.groupsSource !== source) {
					return;
				}

				this.groupsSource = null;
				this.isGroupsLoading = false;
			});
	}

	private loadCategories(groups: number[]): void {
		if (this.tradingId === null) {
			return;
		}

		if (this.categoriesSource !== null) {
			this.categoriesSource.cancel();
		}

		const source = createCancelSource();

		this.categoriesSource = source;
		this.isCategoriesLoading = true;

		fetchProductLevels(this.buildRequest('category', groups, []), { cancelToken: source.token })
			.then((response) => {
				if (this.categoriesSource !== source) {
					return;
				}

				this.categoryOptions = this.readItems(response);
			})
			.catch((error) => {
				if (isCanceled(error) || this.categoriesSource !== source) {
					return;
				}

				this.categoryOptions = this.emptyOptions();
				this.showError(this.$t(KEY + 'taskForm.levelsLoadError') as string);
			})
			.finally(() => {
				if (this.categoriesSource !== source) {
					return;
				}

				this.categoriesSource = null;
				this.isCategoriesLoading = false;
			});
	}

	/** Любой исход запроса обязан дойти до формы: молчание она примет за «подкатегорий нет» */
	private loadSubcategories(groups: number[], categories: number[]): void {
		if (this.tradingId === null) {
			return;
		}

		if (this.subcategoriesSource !== null) {
			this.subcategoriesSource.cancel();
		}

		const source = createCancelSource();

		this.subcategoriesSource = source;
		this.isSubcategoriesLoading = true;
		this.emitSubcategoriesState();

		fetchProductLevels(this.buildRequest('subcategory', groups, categories), { cancelToken: source.token })
			.then((response) => {
				if (this.subcategoriesSource !== source) {
					return;
				}

				this.setSubcategoryOptions(this.readItems(response));
			})
			.catch((error) => {
				if (isCanceled(error) || this.subcategoriesSource !== source) {
					return;
				}

				this.setSubcategoryOptions(this.emptyOptions());
				this.showError(this.$t(KEY + 'taskForm.levelsLoadError') as string);
			})
			.finally(() => {
				if (this.subcategoriesSource !== source) {
					return;
				}

				this.subcategoriesSource = null;
				this.isSubcategoriesLoading = false;
				this.emitSubcategoriesState();
			});
	}

	private readItems(response: any): MmSelectOption[] {
		const data = response && response.data ? response.data : null;

		return data && Array.isArray(data.items) ? data.items : [];
	}

	/** При отказе отдаём пустой список: у товарных уровней 0 значит «Не выбрано», а не «Все» */
	private emptyOptions(): MmSelectOption[] {
		return [];
	}

	private emitLevels(groups: number[], categories: number[], subCategories: number[]): void {
		const payload = this.cloneLevels({
			groups: groups,
			categories: categories,
			subCategories: subCategories,
		});

		this.draft = payload;
		this.$emit('change', payload);
	}

	private patchDraft(groups: number[], categories: number[], subCategories: number[]): void {
		this.draft = {
			groups: groups.slice(),
			categories: categories.slice(),
			subCategories: subCategories.slice(),
		};
		this.emitDraftChange();
	}

	private syncDraftFromValue(): void {
		this.draft = this.cloneLevels(this.value);
		this.emitDraftChange();
	}

	private emitDraftChange(): void {
		this.$emit('draft-change', this.cloneLevels(this.draft));
	}

	private cloneLevels(source: MmTaskCreateLevels): MmTaskCreateLevels {
		return {
			groups: source.groups.slice(),
			categories: source.categories.slice(),
			subCategories: source.subCategories.slice(),
		};
	}

	private levelsEqual(left: MmTaskCreateLevels, right: MmTaskCreateLevels): boolean {
		return this.isSameIdList(left.groups, right.groups)
			&& this.isSameIdList(left.categories, right.categories)
			&& this.isSameIdList(left.subCategories, right.subCategories);
	}

	private setSubcategoryOptions(items: MmSelectOption[]): void {
		this.subcategoryOptions = items;
		this.emitSubcategoriesState();
	}

	private emitSubcategoriesState(): void {
		const payload: MmSubcategoriesState = {
			loading: this.isSubcategoriesLoading,
			options: this.subcategoryOptions.slice(),
		};

		this.$emit('subcategories-state', payload);
	}

	private cancelPendingRequests(): void {
		if (this.groupsSource !== null) {
			this.groupsSource.cancel();
			this.groupsSource = null;
		}

		if (this.categoriesSource !== null) {
			this.categoriesSource.cancel();
			this.categoriesSource = null;
		}

		if (this.subcategoriesSource !== null) {
			this.subcategoriesSource.cancel();
			this.subcategoriesSource = null;
		}

		this.isGroupsLoading = false;
		this.isCategoriesLoading = false;
		this.isSubcategoriesLoading = false;
	}

	/** Сравниваем отсортированные копии, а не ссылки: иначе каскад справочников зацикливается */
	private isSameIdList(a: number[], b: number[]): boolean {
		if (a.length !== b.length) {
			return false;
		}

		const left = a.slice().sort();
		const right = b.slice().sort();

		for (let i = 0; i < left.length; i += 1) {
			if (left[i] !== right[i]) {
				return false;
			}
		}

		return true;
	}
}
